import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, ShieldCheck, Zap, Download, ImageIcon, Video, Loader2, Clock, BadgeCheck, AlertTriangle, X, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function useSystemTheme() {
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = (dark) => document.documentElement.classList.toggle('dark', dark);
    apply(mq.matches);
    const handler = (e) => apply(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
}

function monthsOnPlatform(createdAt) {
  if (!createdAt) return null;
  const created = new Date(createdAt);
  const now = new Date();
  const months = (now.getFullYear() - created.getFullYear()) * 12 + (now.getMonth() - created.getMonth());
  if (months < 1) return 'New creator';
  if (months === 1) return '1 month on platform';
  if (months < 12) return `${months} months on platform`;
  const yrs = Math.floor(months / 12);
  return `${yrs} year${yrs > 1 ? 's' : ''} on platform`;
}

function formatSize(bytes) {
  if (!bytes) return null;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

const AbuseModal = ({ onClose, linkId }) => {
  const [form, setForm] = useState({ reporter_name: '', reporter_email: '', description: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.reporter_name.trim() || !form.reporter_email.trim() || !form.description.trim()) {
      setErr('Please fill in all fields.');
      return;
    }
    setSubmitting(true); setErr('');
    try {
      await axios.post(`${API_URL}/api/reports/abuse`, { ...form, link_id: linkId, page_url: window.location.href });
      setSubmitted(true);
    } catch { setErr('Something went wrong. Please try again.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/85 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-2xl p-6 relative bg-white dark:bg-[#111] border border-neutral-200 dark:border-white/[0.08]" data-testid="abuse-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-neutral-400 dark:text-white/30 hover:text-neutral-600 dark:hover:text-white/60 transition-colors" data-testid="close-modal-btn"><X className="h-4 w-4" /></button>
        {submitted ? (
          <div className="text-center py-4" data-testid="report-success">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20">
              <CheckCircle className="h-5 w-5 text-emerald-500 dark:text-emerald-400" />
            </div>
            <h3 className="text-neutral-900 dark:text-white font-semibold mb-2">Report Received</h3>
            <p className="text-neutral-500 dark:text-white/40 text-sm leading-relaxed">Your issue will be resolved within <span className="text-neutral-700 dark:text-white/60">24 hours</span>.</p>
            <button onClick={onClose} className="mt-5 px-6 py-2 rounded-full text-sm text-neutral-500 dark:text-white/50 border border-neutral-200 dark:border-white/10 hover:border-neutral-400 dark:hover:border-white/20 transition-all" data-testid="close-success-btn">Close</button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-4 w-4 text-amber-500 dark:text-amber-400" />
              <h3 className="text-neutral-900 dark:text-white font-semibold text-sm">Report Abuse</h3>
            </div>
            <p className="text-neutral-500 dark:text-white/35 text-xs mb-4 leading-relaxed">Encountered something inappropriate? Let us know and we'll investigate within 24 hours.</p>
            <form onSubmit={submit} className="space-y-3" data-testid="abuse-form">
              {[['reporter_name', 'text', 'Your Name', 'John Doe'], ['reporter_email', 'email', 'Your Email', 'you@example.com']].map(([key, type, label, ph]) => (
                <div key={key}>
                  <label className="block text-neutral-500 dark:text-white/35 text-xs mb-1">{label}</label>
                  <input type={type} placeholder={ph} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    className="w-full rounded-xl px-3.5 py-2.5 text-sm text-neutral-900 dark:text-white placeholder-neutral-300 dark:placeholder-white/20 bg-neutral-50 dark:bg-white/[0.05] border border-neutral-200 dark:border-white/10 focus:border-neutral-400 dark:focus:border-white/25 focus:outline-none transition-colors"
                    data-testid={`${key}-input`} />
                </div>
              ))}
              <div>
                <label className="block text-neutral-500 dark:text-white/35 text-xs mb-1">What happened?</label>
                <textarea placeholder="Describe the issue..." value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={3} className="w-full rounded-xl px-3.5 py-2.5 text-sm text-neutral-900 dark:text-white placeholder-neutral-300 dark:placeholder-white/20 bg-neutral-50 dark:bg-white/[0.05] border border-neutral-200 dark:border-white/10 focus:border-neutral-400 dark:focus:border-white/25 focus:outline-none transition-colors resize-none"
                  data-testid="reporter-description-input" />
              </div>
              {err && <p className="text-red-500 dark:text-red-400/70 text-xs">{err}</p>}
              <button type="submit" disabled={submitting}
                className="w-full py-2.5 rounded-xl text-sm font-medium transition-all border bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-300/80 disabled:opacity-50"
                data-testid="submit-report-btn">
                {submitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const LinkPurchase = () => {
  const { shortLink } = useParams();
  const navigate = useNavigate();
  useSystemTheme();
  const [link, setLink] = useState(null);
  const [creatorInfo, setCreatorInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [savedCard, setSavedCard] = useState(null);
  const [quickPaying, setQuickPaying] = useState(false);
  const [activeThumb, setActiveThumb] = useState(0);
  const [showAbuse, setShowAbuse] = useState(false);
  const carouselRef = useRef(null);

  useEffect(() => {
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) favicon.href = '/favicon.svg';
    const fetchLink = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/links/by-slug/${shortLink}`);
        setLink(data);
        setCreatorInfo(data.creator_info);
        document.title = `${data.title || 'Unlock Content'} | Unlocky`;
      } catch (err) {
        if (err.response?.status === 410) {
          setError(err.response.data.detail === 'link_deleted' ? 'link_deleted' : 'link_disabled');
        } else {
          setError('not_found');
        }
      } finally { setLoading(false); }
    };
    fetchLink();
    return () => { document.title = 'VFans Media'; if (favicon) favicon.href = '/vfans-logo.svg'; };
  }, [shortLink]);

  const handlePurchase = async () => {
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    setPurchasing(true); setError(null);
    try {
      const checkRes = await axios.post(`${API_URL}/api/links/${link.id}/check-access`, { email });
      if (checkRes.data.has_access) {
        localStorage.setItem('buyer_email', email);
        window.location.href = `${window.location.origin}/purchase/success?session_id=${checkRes.data.session_id}&link_id=${link.id}`;
        return;
      }
      // Check for saved card (demo links only)
      try {
        const cardRes = await axios.post(`${API_URL}/api/links/${link.id}/check-saved-card`, { email });
        if (cardRes.data.has_saved_card) {
          setSavedCard({ last4: cardRes.data.last4, brand: cardRes.data.brand });
          setPurchasing(false);
          return;
        }
      } catch (_) {}
      // Normal Stripe checkout
      localStorage.setItem('buyer_email', email);
      const { data } = await axios.post(`${API_URL}/api/links/${link.id}/checkout`, { email });
      window.location.href = data.url;
    } catch { setError('Failed to start checkout. Please try again.'); setPurchasing(false); }
  };

  const handleQuickPay = async () => {
    setQuickPaying(true); setError(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/links/${link.id}/quick-purchase`, { email });
      // Payment succeeded - redirect to success page
      localStorage.setItem('buyer_email', email);
      window.location.href = `${window.location.origin}/purchase/success?session_id=${data.payment_intent_id}&link_id=${link.id}`;
    } catch (err) {
      // Payment failed - redirect to Stripe checkout immediately
      try {
        localStorage.setItem('buyer_email', email);
        const { data } = await axios.post(`${API_URL}/api/links/${link.id}/checkout`, { email });
        window.location.href = data.url;
      } catch (checkoutErr) {
        // Fallback checkout also failed - show error on current page
        setQuickPaying(false);
        setError('Payment failed. Please try again.');
      }
    }
  };

  const handleUseDifferentCard = async () => {
    setSavedCard(null);
    setPurchasing(true); setError(null);
    try {
      localStorage.setItem('buyer_email', email);
      const { data } = await axios.post(`${API_URL}/api/links/${link.id}/checkout`, { email });
      window.location.href = data.url;
    } catch { setError('Failed to start checkout. Please try again.'); setPurchasing(false); }
  };

  const handleCarouselScroll = (e) => {
    const el = e.currentTarget;
    const idx = Math.round(el.scrollLeft / el.offsetWidth);
    if (idx !== activeThumb) setActiveThumb(idx);
  };

  const scrollToThumb = (i) => {
    if (carouselRef.current) {
      carouselRef.current.scrollTo({ left: i * carouselRef.current.offsetWidth, behavior: 'smooth' });
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-black" data-testid="link-purchase-loading">
      <Loader2 className="h-7 w-7 animate-spin text-neutral-300 dark:text-white/30" />
    </div>
  );

  if (error && !link) {
    const isDisabled = error === 'link_disabled';
    const isDeleted = error === 'link_deleted';
    return (
      <div className="min-h-screen flex items-center justify-center p-5 bg-neutral-50 dark:bg-black" data-testid="link-purchase-error">
        <div className="text-center max-w-xs p-8 rounded-3xl bg-white dark:bg-white/[0.04] border border-neutral-200 dark:border-white/[0.07]">
          <div className="text-4xl mb-4">{isDeleted ? '🗑️' : isDisabled ? '🚫' : '🔍'}</div>
          <h1 className="text-neutral-900 dark:text-white font-semibold mb-2">{isDeleted ? 'Link removed' : isDisabled ? 'Link taken down' : 'Not found'}</h1>
          <p className="text-neutral-500 dark:text-white/40 text-sm mb-5">{isDeleted ? 'This link has been permanently taken down.' : isDisabled ? 'This link has been taken down by the creator.' : 'This link does not exist.'}</p>
          <button onClick={() => navigate('/')} className="px-5 py-2 rounded-full text-neutral-600 dark:text-white/70 text-sm border border-neutral-200 dark:border-white/10 hover:border-neutral-400 dark:hover:border-white/20 transition-all" data-testid="go-home-btn">Go Home</button>
        </div>
      </div>
    );
  }

  const files = link?.files || [];
  const imgCount = files.filter(f => f.type === 'image').length || (link?.file_type === 'image' ? 1 : 0);
  const vidCount = files.filter(f => f.type === 'video').length || (link?.file_type === 'video' ? 1 : 0);
  const totalSize = files.reduce((s, f) => s + (f.size || 0), 0);

  const thumbnails = (() => {
    const list = [];
    if (files.length > 0) {
      files.forEach(f => {
        if (f.preview_url) list.push({ src: `${API_URL}${f.preview_url}`, type: f.type || 'image' });
      });
    }
    if (list.length === 0 && link?.preview_url) list.push({ src: `${API_URL}${link.preview_url}`, type: link?.file_type || 'image' });
    return list;
  })();

  const timeOnPlatform = monthsOnPlatform(creatorInfo?.created_at);
  const sizeStr = formatSize(totalSize);
  const creatorInitial = creatorInfo?.name?.charAt(0)?.toUpperCase() || 'C';
  const finalPrice = link?.price || 0;

  return (
    <>
      <style>{`
        .thumb-active { border: 2px solid #10b981 !important; }
        .thumb-item { border: 2px solid transparent; transition: border-color 0.2s; cursor: pointer; }
        .thumb-item:hover { border-color: rgba(16,185,129,0.4); }
        .unlock-btn {
          background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
          box-shadow: 0 4px 24px rgba(16,185,129,0.35), inset 0 1px 0 rgba(255,255,255,0.2);
          transition: all 0.25s;
        }
        .unlock-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 32px rgba(16,185,129,0.45); }
        .unlock-btn:active:not(:disabled) { transform: translateY(0); }
        .carousel-scroll::-webkit-scrollbar { display: none; }
        .protected-img { -webkit-user-select:none; user-select:none; -webkit-touch-callout:none; pointer-events:none; -webkit-user-drag:none; }
        .protected-wrap { -webkit-user-select:none; user-select:none; -webkit-touch-callout:none; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        .fade-up { animation: fadeUp 0.4s ease both; }
        .fade-up-1 { animation-delay: 0.05s; }
        .fade-up-2 { animation-delay: 0.1s; }
        .fade-up-3 { animation-delay: 0.15s; }
      `}</style>

      <div className="min-h-screen bg-neutral-50 dark:bg-black flex flex-col items-center pb-10" data-testid="link-purchase-page">
        <div className="w-full max-w-sm px-4 pt-6">

          {/* Preview carousel */}
          {thumbnails.length > 0 ? (
            <div
              ref={carouselRef}
              className="carousel-scroll relative rounded-3xl mb-3 fade-up protected-wrap bg-neutral-200 dark:bg-[#111] overflow-hidden"
              style={{ height: '65vw', maxHeight: '300px', overflowX: 'auto', overflowY: 'hidden', scrollSnapType: 'x mandatory', scrollBehavior: 'smooth', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
              onScroll={handleCarouselScroll}
              onContextMenu={e => e.preventDefault()}
              data-testid="main-preview"
            >
              <div style={{ display: 'flex', height: '100%', width: `${thumbnails.length * 100}%` }}>
                {thumbnails.map((thumb, i) => (
                  <div key={i} style={{ width: `${100 / thumbnails.length}%`, height: '100%', position: 'relative', flexShrink: 0, scrollSnapAlign: 'start' }}>
                    <div className="protected-img" style={{ width: '100%', height: '100%', backgroundImage: `url(${thumb.src})`, backgroundSize: 'cover', backgroundPosition: 'center', transform: 'scale(1.0)' }} aria-hidden="true" />
                    <div className="absolute inset-0 bg-black/30" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center bg-white/12 backdrop-blur-xl border border-white/35">
                        <Lock className="w-[22px] h-[22px] text-white" />
                      </div>
                      {i === 0 && (
                        <>
                          <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-white/65 m-0">Unlock for</p>
                          <p className="text-[22px] font-bold text-white m-0 leading-none">${finalPrice?.toFixed(2)} <span className="text-xs font-normal text-white/45">USD</span></p>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="relative rounded-3xl overflow-hidden mb-3 fade-up bg-gradient-to-b from-neutral-200 to-neutral-100 dark:from-[#1a1a2e] dark:to-[#0f0f1a]" style={{ height: '65vw', maxHeight: '300px' }} data-testid="main-preview">
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <div className="w-14 h-14 rounded-full flex items-center justify-center bg-neutral-300/50 dark:bg-white/10 backdrop-blur-xl border border-neutral-400/30 dark:border-white/18">
                  <Lock className="w-[22px] h-[22px] text-neutral-600 dark:text-white" />
                </div>
                <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-neutral-500 dark:text-white/60 m-0">Unlock for</p>
                <p className="text-[22px] font-bold text-neutral-900 dark:text-white m-0 leading-none">${finalPrice?.toFixed(2)} <span className="text-xs font-normal text-neutral-400 dark:text-white/45">USD</span></p>
              </div>
            </div>
          )}

          {/* Thumbnail strip */}
          {thumbnails.length > 1 && (
            <div className="flex gap-2 mb-5 overflow-x-auto pb-1 fade-up fade-up-1 protected-wrap" style={{ scrollbarWidth: 'none' }} onContextMenu={e => e.preventDefault()} data-testid="thumbnail-strip">
              {thumbnails.map((thumb, i) => (
                <button key={i} onClick={() => scrollToThumb(i)}
                  className={`flex-shrink-0 rounded-xl overflow-hidden thumb-item relative ${i === activeThumb ? 'thumb-active' : ''}`}
                  style={{ width: 72, height: 72 }}
                  data-testid={`thumb-${i}`}>
                  <div className="absolute inset-0 bg-neutral-200 dark:bg-[#111]" />
                  <div className="protected-img relative z-10" style={{ width: '100%', height: '100%', backgroundImage: `url(${thumb.src})`, backgroundSize: 'cover', backgroundPosition: 'center', transform: 'scale(1.0)' }} aria-hidden="true" />
                  <div className="absolute bottom-1 right-1 z-20 bg-black/55 backdrop-blur rounded-md px-1.5 py-0.5 flex items-center justify-center">
                    {thumb.type === 'video' ? <Video className="w-3 h-3 text-white" /> : <ImageIcon className="w-3 h-3 text-white" />}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Info card */}
          <div className="rounded-3xl overflow-hidden fade-up fade-up-2 bg-white dark:bg-white/[0.04] border border-neutral-200 dark:border-white/[0.07]">

            {/* Creator row */}
            <a href={`/c/${creatorInfo?.username || creatorInfo?.id}`} className="flex items-center gap-3 px-5 py-4 hover:bg-neutral-50 dark:hover:bg-white/[0.02] transition-colors" data-testid="creator-profile-link">
              <div className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center bg-neutral-100 dark:bg-white/[0.08] border border-neutral-200 dark:border-white/10">
                {creatorInfo?.profile_picture
                  ? <img src={creatorInfo.profile_picture.startsWith('/api') ? `${API_URL}${creatorInfo.profile_picture}` : creatorInfo.profile_picture} alt="" className="w-full h-full object-cover" />
                  : <span className="text-sm font-semibold text-neutral-400 dark:text-white/50">{creatorInitial}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-semibold text-neutral-900 dark:text-white text-sm leading-none" data-testid="creator-name">{creatorInfo?.name || 'Creator'}</span>
                  <BadgeCheck className="w-4 h-4 text-emerald-500 dark:text-emerald-400 flex-shrink-0" />
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[11px] text-neutral-500 dark:text-white/35 flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Verified</span>
                  {timeOnPlatform && (
                    <>
                      <span className="text-neutral-300 dark:text-white/20 text-[10px]">·</span>
                      <span className="text-[11px] text-neutral-500 dark:text-white/35 flex items-center gap-1"><Clock className="w-3 h-3" />{timeOnPlatform}</span>
                    </>
                  )}
                </div>
              </div>
            </a>

            <div className="h-px bg-neutral-100 dark:bg-white/[0.05]" />

            {/* Title & meta */}
            <div className="px-5 py-4">
              <h1 className="text-base font-bold text-neutral-900 dark:text-white mb-3 leading-snug" data-testid="link-title">{link?.title}</h1>

              <div className="flex items-center gap-2 flex-wrap mb-1">
                {imgCount > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] text-neutral-500 dark:text-white/45 bg-neutral-100 dark:bg-white/[0.06] border border-neutral-200 dark:border-white/[0.08]">
                    <ImageIcon className="w-3 h-3" />{imgCount} image{imgCount !== 1 ? 's' : ''}
                  </span>
                )}
                {vidCount > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] text-neutral-500 dark:text-white/45 bg-neutral-100 dark:bg-white/[0.06] border border-neutral-200 dark:border-white/[0.08]">
                    <Video className="w-3 h-3" />{vidCount} video{vidCount !== 1 ? 's' : ''}
                  </span>
                )}
                {sizeStr && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] text-neutral-500 dark:text-white/45 bg-neutral-100 dark:bg-white/[0.06] border border-neutral-200 dark:border-white/[0.08]">
                    <Download className="w-3 h-3" />{sizeStr}
                  </span>
                )}
              </div>

              {link?.description && <p className="text-xs text-neutral-500 dark:text-white/35 mt-2 leading-relaxed" data-testid="link-description">{link.description}</p>}
            </div>

            <div className="h-px bg-neutral-100 dark:bg-white/[0.05]" />

            {/* Purchase section */}
            <div className="px-5 py-5 fade-up fade-up-3">

              <p className="text-3xl font-bold text-neutral-900 dark:text-white mb-5 leading-none" data-testid="link-price">
                ${finalPrice?.toFixed(2)} <span className="text-sm font-normal text-neutral-400 dark:text-white/30 ml-0.5">USD</span>
              </p>

              <div className="mb-4">
                <label className="block text-xs font-medium text-neutral-500 dark:text-white/35 mb-1.5 uppercase tracking-wider">Email address</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(null); }}
                  className="w-full px-4 py-3 rounded-xl text-sm bg-neutral-50 dark:bg-white/[0.06] text-neutral-900 dark:text-white border border-neutral-200 dark:border-white/10 placeholder:text-neutral-300 dark:placeholder:text-white/25 focus:border-emerald-400 dark:focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/10 transition-all"
                  data-testid="email-input"
                />
              </div>

              {savedCard ? (
                <div>
                  <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-2xl bg-neutral-50 dark:bg-white/[0.04] border border-neutral-200 dark:border-white/[0.07]">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-neutral-100 dark:bg-white/[0.08] border border-neutral-200 dark:border-white/10">
                      <ShieldCheck className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-900 dark:text-white leading-none">
                        {savedCard.brand?.charAt(0).toUpperCase() + savedCard.brand?.slice(1)} •••• {savedCard.last4}
                      </p>
                      <p className="text-[11px] text-neutral-400 dark:text-white/30 mt-0.5">Saved card</p>
                    </div>
                  </div>
                  {error && <p className="text-red-500 dark:text-red-400/80 text-xs mb-3">{error}</p>}
                  <button onClick={handleQuickPay} disabled={quickPaying}
                    className="unlock-btn w-full py-4 rounded-2xl text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 mb-3"
                    data-testid="quick-pay-btn">
                    {quickPaying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                    {quickPaying ? 'Processing...' : `Pay $${finalPrice?.toFixed(2)} instantly`}
                  </button>
                  <button onClick={handleUseDifferentCard}
                    className="w-full text-center text-xs text-neutral-400 dark:text-white/30 hover:text-neutral-600 dark:hover:text-white/50 transition-colors underline underline-offset-2">
                    Use a different card
                  </button>
                </div>
              ) : (
                <>
                  {error && <p className="text-red-500 dark:text-red-400/80 text-xs mb-3" data-testid="purchase-error">{error}</p>}
                  <button onClick={handlePurchase} disabled={purchasing}
                    className="unlock-btn w-full py-4 rounded-2xl text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="unlock-btn">
                    {purchasing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                    {purchasing ? 'Processing...' : `Unlock for $${finalPrice?.toFixed(2)}`}
                  </button>
                </>
              )}

              <div className="mt-4 space-y-1.5">
                <div className="flex items-center justify-center gap-5 text-[11px] text-neutral-400 dark:text-white/25">
                  <span className="flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" />Secure payment</span>
                  <span className="flex items-center gap-1.5"><Zap className="w-3 h-3" />Instant access</span>
                </div>
              </div>

              <p className="text-center text-[10px] text-neutral-400 dark:text-white/20 mt-4">
                By purchasing, you agree to our{' '}
                <a href="/terms-of-service" className="underline text-neutral-500 dark:text-white/35 hover:text-neutral-700 dark:hover:text-white/50 transition-colors">Terms of Service</a>
              </p>

              <div className="mt-3 text-center">
                <button onClick={() => setShowAbuse(true)} className="text-[10px] text-neutral-400 dark:text-white/30 hover:text-neutral-600 dark:hover:text-white/55 transition-colors underline underline-offset-2 decoration-neutral-300 dark:decoration-white/15" data-testid="report-abuse-btn">
                  Need help? Report abuse
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAbuse && <AbuseModal onClose={() => setShowAbuse(false)} linkId={link?.id} />}
    </>
  );
};

export default LinkPurchase;
